// Table reservations (F-RSV-N1 / F-RSV-N2) — types, settings parsing and the
// pure helpers the Reservations page, the floor and the customer profile
// share. The server is the authority for every rule; these only shape UI.

export const RESERVATIONS_SETTING_KEY = 'reservations.config'

/** Every tenant runs in Pakistan; the backend hardcodes Asia/Karachi too. */
export const BUSINESS_TZ = 'Asia/Karachi'
const PKT_OFFSET = '+05:00'

export type ReservationStatus = 'pending' | 'confirmed' | 'seated' | 'completed' | 'cancelled' | 'no_show'
export type ReservationSource = 'phone' | 'walk_in' | 'online' | 'whatsapp' | 'instagram' | 'other'

export interface ReservationTable {
  id: string
  table_number: string
  seating_capacity: number
}

export interface Reservation {
  id: string
  guest_name: string
  guest_phone?: string
  guest_email?: string
  customer_id?: string
  party_size: number
  starts_at: string
  ends_at: string
  duration_minutes: number
  notes?: string
  source: ReservationSource
  status: ReservationStatus
  capacity_override: boolean
  order_id?: string
  order_number?: string
  cancel_reason?: string
  confirmation_sent_at?: string
  confirmed_at?: string
  seated_at?: string
  completed_at?: string
  cancelled_at?: string
  no_show_at?: string
  created_by_name?: string
  updated_by_name?: string
  created_at: string
  updated_at: string
  tables: ReservationTable[]
  capacity: number
}

export interface ReservationWrite {
  guest_name: string
  guest_phone?: string
  guest_email?: string
  party_size: number
  starts_at: string
  duration_minutes?: number
  table_ids: string[]
  notes?: string
  source: ReservationSource
  capacity_override?: boolean
}

export interface ReservationHold {
  table_id: string
  reservation_id: string
  guest_name: string
  party_size: number
  starts_at: string
  ends_at: string
}

export interface GuestHistory {
  customer_id?: string
  name?: string
  phone: string
  visit_count: number
  last_visit_at?: string
  reservation_count: number
  no_show_count: number
  cancelled_count: number
  last_reservation_at?: string
}

export type WaitlistStatus = 'waiting' | 'notified' | 'seated' | 'removed'

export interface WaitlistEntry {
  id: string
  business_date: string
  guest_name: string
  guest_phone?: string
  customer_id?: string
  party_size: number
  quoted_wait_minutes?: number
  notes?: string
  status: WaitlistStatus
  table_id?: string
  table_number?: string
  order_id?: string
  notified_at?: string
  seated_at?: string
  removed_at?: string
  created_at: string
}

export interface ReservationsSummary {
  enabled: boolean
  online_enabled: boolean
  pending: number
  today: number
}

export interface HoursWindow {
  closed?: boolean
  open: string
  close: string
}

export interface ReservationsConfig {
  enabled: boolean
  default_duration_minutes: number
  hold_minutes: number
  no_show_grace_minutes: number
  online: {
    enabled: boolean
    days_ahead: number
    slot_interval_minutes: number
    min_notice_minutes: number
    max_party_size: number
    allow_combining_tables: boolean
    confirmation: 'auto' | 'manual'
    hours_source: 'storefront' | 'custom'
    hours?: HoursWindow[]
  }
}

export const DEFAULT_RESERVATIONS_CONFIG: ReservationsConfig = {
  enabled: false,
  default_duration_minutes: 90,
  hold_minutes: 15,
  no_show_grace_minutes: 15,
  online: {
    enabled: false,
    days_ahead: 30,
    slot_interval_minutes: 30,
    min_notice_minutes: 60,
    max_party_size: 8,
    allow_combining_tables: false,
    confirmation: 'manual',
    hours_source: 'storefront',
  },
}

const num = (v: unknown, def: number) => (typeof v === 'number' && Number.isFinite(v) ? v : def)

/** Reads the stored setting; anything malformed reads as OFF with defaults. */
export function reservationsConfigFrom(raw: unknown): ReservationsConfig {
  const d = DEFAULT_RESERVATIONS_CONFIG
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...d, online: { ...d.online } }
  const r = raw as Record<string, unknown>
  const o = (r.online && typeof r.online === 'object' ? r.online : {}) as Record<string, unknown>
  const hours = Array.isArray(o.hours) && o.hours.length === 7
    ? (o.hours as HoursWindow[]).map((h) => ({ closed: h?.closed === true, open: String(h?.open ?? ''), close: String(h?.close ?? '') }))
    : undefined
  return {
    enabled: r.enabled === true,
    default_duration_minutes: num(r.default_duration_minutes, d.default_duration_minutes),
    hold_minutes: num(r.hold_minutes, d.hold_minutes),
    no_show_grace_minutes: num(r.no_show_grace_minutes, d.no_show_grace_minutes),
    online: {
      enabled: o.enabled === true,
      days_ahead: num(o.days_ahead, d.online.days_ahead),
      slot_interval_minutes: num(o.slot_interval_minutes, d.online.slot_interval_minutes),
      min_notice_minutes: num(o.min_notice_minutes, d.online.min_notice_minutes),
      max_party_size: num(o.max_party_size, d.online.max_party_size),
      allow_combining_tables: o.allow_combining_tables === true,
      confirmation: o.confirmation === 'auto' ? 'auto' : 'manual',
      hours_source: o.hours_source === 'custom' ? 'custom' : 'storefront',
      ...(hours ? { hours } : {}),
    },
  }
}

// ── Time (always the business timezone, never the device's) ──────────────

const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TZ, hour: 'numeric', minute: '2-digit' })
const dateFmt = new Intl.DateTimeFormat('en-GB', { timeZone: BUSINESS_TZ, weekday: 'short', day: 'numeric', month: 'short' })
const isoDateFmt = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
const hhmmFmt = new Intl.DateTimeFormat('en-GB', { timeZone: BUSINESS_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/** "8:30 PM" in Pakistan time. */
export function pktTime(iso: string | Date): string {
  return timeFmt.format(typeof iso === 'string' ? new Date(iso) : iso)
}
/** "Fri 6 Nov" in Pakistan time. */
export function pktDate(iso: string | Date): string {
  return dateFmt.format(typeof iso === 'string' ? new Date(iso) : iso)
}
/** "2026-11-06" — the Pakistan calendar date of an instant. */
export function pktIsoDate(iso: string | Date): string {
  return isoDateFmt.format(typeof iso === 'string' ? new Date(iso) : iso)
}
/** "20:30" — the Pakistan 24h clock of an instant. */
export function pktHHMM(iso: string | Date): string {
  return hhmmFmt.format(typeof iso === 'string' ? new Date(iso) : iso)
}

/** RFC3339 instant for a date + "HH:MM" picked on the Reservations page. */
export function pktInstant(dateISO: string, hhmm: string): string {
  return `${dateISO}T${hhmm}:00${PKT_OFFSET}`
}

/** Shift an ISO date by whole days (calendar arithmetic, no timezone). */
export function addDaysISO(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return t.toISOString().slice(0, 10)
}

/**
 * The business date "today" — Pakistan time shifted back by the tenant's
 * day-boundary hour, the same rule as the backend's util.BusinessDate.
 */
export function businessTodayISO(now: Date = new Date(), boundaryHour = 5): string {
  return pktIsoDate(new Date(now.getTime() - boundaryHour * 3600_000))
}

/** Time options for the booking form: every 15 minutes, 24h. */
export function timeOptions(stepMinutes = 15): string[] {
  const out: string[] = []
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  }
  return out
}

/** "20:30" → "8:30 PM" for option labels. */
export function hhmmLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

// ── Grouping ──────────────────────────────────────────────────────────────

export interface SlotGroup {
  key: string // "20:30"
  label: string // "8:30 PM"
  items: Reservation[]
}

/** Bookings grouped by their start time, in time order. */
export function groupBySlot(list: Reservation[]): SlotGroup[] {
  const sorted = [...list].sort((a, b) => a.starts_at.localeCompare(b.starts_at) || a.created_at.localeCompare(b.created_at))
  const groups: SlotGroup[] = []
  for (const r of sorted) {
    const key = new Date(r.starts_at).toISOString()
    const last = groups[groups.length - 1]
    if (last && last.key === key) last.items.push(r)
    else groups.push({ key, label: pktTime(r.starts_at), items: [r] })
  }
  return groups
}

export const STATUS_LABEL: Record<ReservationStatus, string> = {
  pending: 'Awaiting approval',
  confirmed: 'Confirmed',
  seated: 'Seated',
  completed: 'Finished',
  cancelled: 'Cancelled',
  no_show: 'No-show',
}

export const SOURCE_LABEL: Record<ReservationSource, string> = {
  phone: 'Phone',
  walk_in: 'Walk-in',
  online: 'Online',
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  other: 'Other',
}

export const STAFF_SOURCES: ReservationSource[] = ['phone', 'walk_in', 'whatsapp', 'instagram', 'other']

/** True when the booking still holds its table(s) (shown in the active list). */
export function isActiveReservation(r: Pick<Reservation, 'status'>): boolean {
  return r.status === 'pending' || r.status === 'confirmed' || r.status === 'seated'
}

/** Mirrors the backend rule: no-show only after start + grace. */
export function canMarkNoShow(r: Pick<Reservation, 'status' | 'starts_at'>, graceMinutes: number, now = Date.now()): boolean {
  return r.status === 'confirmed' && now >= new Date(r.starts_at).getTime() + graceMinutes * 60_000
}

// ── Floor ─────────────────────────────────────────────────────────────────

/** "Reserved 8:30 · Ahmed (4)" — the floor's short label for a hold. */
export function holdLabel(h: Pick<ReservationHold, 'starts_at' | 'guest_name' | 'party_size'>): string {
  const first = h.guest_name.trim().split(/\s+/)[0] || h.guest_name
  const t = pktTime(h.starts_at).replace(/\s?(AM|PM)$/i, '')
  return `Reserved ${t} · ${first} (${h.party_size})`
}

export function holdsByTable(holds: ReservationHold[] | undefined): Map<string, ReservationHold> {
  const m = new Map<string, ReservationHold>()
  for (const h of holds ?? []) if (!m.has(h.table_id)) m.set(h.table_id, h)
  return m
}

// ── WhatsApp click-out (wa.me — never a business-initiated template) ──────

/** wa.me wants digits only, country code first: 0301 2345678 → 923012345678. */
export function waDigits(phone: string | undefined | null): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('0')) return `92${digits.slice(1)}`
  if (digits.length === 10 && digits.startsWith('3')) return `92${digits}`
  if (digits.length === 12 && digits.startsWith('92')) return digits
  if (phone.trim().startsWith('+') && digits.length >= 8 && digits.length <= 15) return digits
  if (digits.startsWith('00') && digits.length >= 10) return digits.slice(2)
  return null
}

export function waLink(phone: string | undefined | null, text: string): string | null {
  const d = waDigits(phone)
  return d ? `https://wa.me/${d}?text=${encodeURIComponent(text)}` : null
}

export function confirmationMessage(r: Pick<Reservation, 'guest_name' | 'starts_at' | 'party_size'>, restaurant: string): string {
  const first = r.guest_name.trim().split(/\s+/)[0] || r.guest_name
  const at = restaurant.trim() ? ` at ${restaurant.trim()}` : ''
  return `Hi ${first}, your table${at} is confirmed for ${pktDate(r.starts_at)} at ${pktTime(r.starts_at)}, ${r.party_size} ${r.party_size === 1 ? 'guest' : 'guests'}. See you then!`
}

export function tableReadyMessage(e: Pick<WaitlistEntry, 'guest_name'>, restaurant: string): string {
  const first = e.guest_name.trim().split(/\s+/)[0] || e.guest_name
  const at = restaurant.trim() ? ` at ${restaurant.trim()}` : ''
  return `Hi ${first}, your table${at} is ready. Please come to the host stand.`
}

/** Minutes elapsed since an instant, floored. */
export function minutesSinceISO(iso: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000))
}

/** The tenant's name for guest messages — same lookup order as the backend's reportsBrandName. */
export function restaurantNameFrom(all: Record<string, unknown> | undefined): string {
  for (const k of ['restaurant_name', 'receipt_business_name', 'tenant_display_name']) {
    const v = all?.[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}
